import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adminApi } from "@/api/admin-api";
import type { IncidentListInput, LogSearchInput } from "@/api/types";

export function useApps() {
  return useQuery({ queryKey: ["apps"], queryFn: adminApi.apps });
}

export function useIngestionHealth() {
  return useQuery({ queryKey: ["ingestion-health"], queryFn: adminApi.ingestionHealth });
}

export function useAuditEvents() {
  return useQuery({ queryKey: ["audit-events"], queryFn: adminApi.auditEvents });
}

export function useIncidents(input: IncidentListInput) {
  return useQuery({
    queryKey: ["incidents", input],
    queryFn: () => adminApi.incidents(input)
  });
}

export function useClientStations(clientId: number) {
  return useQuery({
    queryKey: ["client-stations", clientId],
    queryFn: () => adminApi.clientStations(clientId),
    enabled: clientId > 0
  });
}

export function useClientIncidents(clientId: number) {
  return useQuery({
    queryKey: ["client-incidents", clientId],
    queryFn: () => adminApi.clientIncidents(clientId),
    enabled: clientId > 0
  });
}

export function useClientLatestErrors(clientId: number) {
  return useQuery({
    queryKey: ["client-latest-errors", clientId],
    queryFn: () => adminApi.clientLatestErrors(clientId),
    enabled: clientId > 0
  });
}

export function useLogSearch(input: LogSearchInput) {
  return useQuery({
    queryKey: ["logs-search", input],
    queryFn: () => adminApi.logsSearch(input),
    enabled: (input.clientId === undefined || input.clientId > 0) && (input.clientId !== undefined || Boolean(input.stationId))
  });
}

export function useHostOverview(clientId: number, hostId: string) {
  return useQuery({
    queryKey: ["host-overview", clientId, hostId],
    queryFn: () => adminApi.hostOverview(clientId, hostId),
    enabled: clientId > 0 && hostId.length > 0
  });
}

export function useHostSignalFamilies(clientId: number, hostId: string) {
  return useQuery({
    queryKey: ["host-signal-families", clientId, hostId],
    queryFn: () => adminApi.hostSignalFamilies(clientId, hostId),
    enabled: clientId > 0 && hostId.length > 0
  });
}

export function useHostDisks(clientId: number, hostId: string) {
  return useQuery({
    queryKey: ["host-disks", clientId, hostId],
    queryFn: () => adminApi.hostDisks(clientId, hostId),
    enabled: clientId > 0 && hostId.length > 0
  });
}

export function useHostProcesses(clientId: number, hostId: string) {
  return useQuery({
    queryKey: ["host-processes", clientId, hostId],
    queryFn: () => adminApi.hostProcesses(clientId, hostId),
    enabled: clientId > 0 && hostId.length > 0
  });
}

export function useHostContainers(clientId: number, hostId: string) {
  return useQuery({
    queryKey: ["host-containers", clientId, hostId],
    queryFn: () => adminApi.hostContainers(clientId, hostId),
    enabled: clientId > 0 && hostId.length > 0
  });
}

export function useHostCorrelation(clientId: number, hostId: string) {
  return useQuery({
    queryKey: ["host-correlation", clientId, hostId],
    queryFn: () => adminApi.hostCorrelation(clientId, hostId),
    enabled: clientId > 0 && hostId.length > 0
  });
}

export function useIncident(incidentId: string) {
  return useQuery({
    queryKey: ["incident", incidentId],
    queryFn: () => adminApi.incident(incidentId),
    enabled: incidentId.length > 0
  });
}

export function useIncidentMutations(incidentId: string, clientId: number) {
  const queryClient = useQueryClient();
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["incident", incidentId] }),
      queryClient.invalidateQueries({ queryKey: ["incidents"] }),
      queryClient.invalidateQueries({ queryKey: ["client-incidents", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["station-incidents"] })
    ]);
  };
  return {
    acknowledge: useMutation({
      mutationFn: () => adminApi.acknowledgeIncident(incidentId),
      onSuccess: invalidate
    }),
    resolve: useMutation({
      mutationFn: (note: string) => adminApi.resolveIncident(incidentId, note),
      onSuccess: invalidate
    }),
    reopen: useMutation({
      mutationFn: () => adminApi.reopenIncident(incidentId),
      onSuccess: invalidate
    }),
    comment: useMutation({
      mutationFn: (body: string) => adminApi.commentIncident(incidentId, body),
      onSuccess: invalidate
    })
  };
}

export function useDebugWindowMutations(stationId: string, clientId: number) {
  const queryClient = useQueryClient();
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["client-stations", clientId] }),
      queryClient.invalidateQueries({ queryKey: ["client-incidents", clientId] })
    ]);
  };
  return {
    create: useMutation({
      mutationFn: (input: { durationMinutes: number; reason: string }) =>
        adminApi.createDebugWindow(stationId, input.durationMinutes, input.reason),
      onSuccess: invalidate
    }),
    revoke: useMutation({
      mutationFn: () => adminApi.revokeDebugWindow(stationId),
      onSuccess: invalidate
    })
  };
}
